// Hold / Release Hold — ONE control for every step that offers either (owner,
// 2026-09-25: "Hold should sit to the left of the Archive button inside the
// invoice modal").
//
// The button, its confirmation and the two requests live here together, so
// every screen and the editor render the same thing and ask the same question.
// The confirmation is `DecisionDialog`, exactly like Deny, Archive and
// Typed Into Point Click Care: "Are you sure?", one sentence of body, a required Category
// above the Reason, and the Reason required only under Other.
//
// WHERE IT SITS is the caller's business, and the rule is one line: immediately
// left of the step's negative action — Archive at Import Control, Deny at the
// three approval tiers. Export Control has no negative action: Hold sits left
// of a divider and Typed Into Point Click Care there (D-311; right
// of Move To Manual Entry from D-247 until then).
//
// HOLD EXISTS AT SEVEN STEPS: Import Control, Controller Approval, the three
// approval tiers, Export Control and Denial Review. Closing Confirmation (and
// Manual Entry, until the step was dropped — D-311) lost it on 2026-09-30
// (owner: "Please remove Hold from all steps after Oversight Approval" —
// D-243). Export Control, which had lost it on 2026-08-19 (D-46), got it back
// the same day (D-247), and Denial Review on 2026-10-08 (D-327). Closing
// Confirmation offered Release Hold alone, for holds placed there before
// 2026-09-30, until code review C-13 (2026-10-17): none can exist.
import { useEffect, useState } from 'react'
import { Button, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { LockClosedFilled, LockClosedRegular } from '@fluentui/react-icons'
import type { ActiveHold } from '../lib/types'
import { HOLD_CATEGORIES, TOASTER_ID } from '../lib/constants'
import { createHold, releaseHold } from '../lib/data'
import { HOLD_BODY, HOLD_REASON_PLACEHOLDER, releaseHoldBody, releaseHoldTitle } from '../lib/holds'
import { errorMessage } from '../api/errors'
import { DecisionDialog } from './DecisionDialog'

/** The toast after a release — this person's, or one found already made (D-263). */
const RELEASED_TOAST = 'The hold was released.'

const useStyles = makeStyles({
  // Active hold on a queue row: amber, so a held row is unmissable even dimmed.
  holdActive: { color: tokens.colorPaletteYellowForeground2 },
})

interface Props {
  invoiceId: string
  /** The invoice's ACTIVE hold (`Invoice.active_hold`), or undefined. */
  hold: ActiveHold | undefined
  /** May this viewer decide at the invoice's step? Hold authority rides it. */
  decidable: boolean
  /** Shown on the disabled button when `decidable` is false. */
  notAuthorizedReason?: string
  /** `button` (default) in a modal action bar — a labelled button with NO
      icon, like every other action there (owner, 2026-09-30, D-245: "drop the
      icons from the Hold, Deny, Approve…"; it had already been dropped at
      Import Control on 2026-09-28). `icon` on a queue row, beside the
      icon-only Approve and Deny, which keeps its lock. */
  variant?: 'button' | 'icon'
  /** Something else on the screen is submitting. */
  disabled?: boolean
  /** The new active hold, or null once released. */
  onChanged: (hold: ActiveHold | null) => void
  /** Where a refusal is shown. A toast when absent. */
  onError?: (message: string) => void
  /** Told when the pop-up opens and closes, so the screen can pause its
      background refresh while it is open (D-160). */
  onDialogOpenChange?: (open: boolean) => void
}

export function HoldControl({
  invoiceId,
  hold,
  decidable,
  notAuthorizedReason,
  variant = 'button',
  disabled = false,
  onChanged,
  onError,
  onDialogOpenChange,
}: Props) {
  const s = useStyles()
  const { dispatchToast } = useToastController(TOASTER_ID)
  const [mode, setMode] = useState<'hold' | 'release' | null>(null)
  // What the dialog says, kept separately from whether it is open, so the
  // surface does not flash the other mode's words on its way out.
  const [shown, setShown] = useState<{ mode: 'hold' | 'release'; hold?: ActiveHold } | null>(null)
  const [submitting, setSubmitting] = useState(false)
  useEffect(() => {
    onDialogOpenChange?.(mode !== null)
  }, [mode, onDialogOpenChange])

  function report(message: string) {
    if (onError) onError(message)
    else
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent: 'error' },
      )
  }

  function open(next: 'hold' | 'release') {
    setShown({ mode: next, hold })
    setMode(next)
  }

  async function submit(reason: string, category?: string) {
    if (!mode) return
    setSubmitting(true)
    try {
      if (mode === 'hold') {
        const placed = await createHold(invoiceId, category ?? '', reason)
        setMode(null)
        dispatchToast(
          <Toast>
            <ToastTitle>The invoice was placed on hold.</ToastTitle>
          </Toast>,
          { intent: 'success' },
        )
        onChanged(placed)
      } else if (shown?.hold) {
        await releaseHold(shown.hold.id)
        setMode(null)
        dispatchToast(
          <Toast>
            <ToastTitle>{RELEASED_TOAST}</ToastTitle>
          </Toast>,
          { intent: 'success' },
        )
        onChanged(null)
      }
    } catch (e) {
      const message = `${mode === 'hold' ? 'Hold' : 'Release Hold'} failed: ${errorMessage(e)}`
      setMode(null)
      report(message)
    } finally {
      setSubmitting(false)
    }
  }

  const busy = disabled || submitting
  const title = hold
    ? releaseHoldTitle(hold, decidable)
    : decidable
      ? 'Keep this invoice at this step until the hold is released.'
      : notAuthorizedReason
  const label = hold ? 'Release Hold' : 'Hold'
  // NAMED ONCE, EXPLAINED IN THE DESCRIPTION (QA C2-04, 2026-09-25). The icon
  // form was named `${label} — ${title}`, and the title already began with the
  // label — "Hold — Hold — keep this invoice…"; the labelled form had no
  // `aria-label`, and a held invoice's Release Hold was read by its tooltip,
  // telling the reader to press Release Hold. Now both forms are named by the
  // label alone, and the tooltip is the description.
  const a11y = { 'aria-label': label, 'aria-description': title }

  const button =
    variant === 'icon' ? (
      <Button
        size="small"
        appearance="subtle"
        icon={hold ? <LockClosedFilled className={s.holdActive} /> : <LockClosedRegular />}
        title={title}
        {...a11y}
        disabled={busy || !decidable}
        onClick={() => open(hold ? 'release' : 'hold')}
      />
    ) : (
      <Button
        title={title}
        {...a11y}
        disabled={busy || !decidable}
        onClick={() => open(hold ? 'release' : 'hold')}
      >
        {label}
      </Button>
    )

  return (
    <>
      {button}
      <DecisionDialog
        open={mode !== null}
        title="Are you sure?"
        body={
          shown?.mode === 'release' && shown.hold ? releaseHoldBody(shown.hold) : HOLD_BODY
        }
        requireReason={shown?.mode !== 'release'}
        reasonLabel="Comment"
        reasonPlaceholder={HOLD_REASON_PLACEHOLDER}
        categories={shown?.mode === 'release' ? undefined : HOLD_CATEGORIES}
        confirmLabel={shown?.mode === 'release' ? 'Release Hold' : 'Hold'}
        submitting={submitting}
        onCancel={() => setMode(null)}
        onConfirm={(reason, category) => void submit(reason, category)}
      />
    </>
  )
}
